import React, { useEffect, useState } from "react";
import "../../styles/quiz/Scenario.css";
import { useNavigate } from "react-router-dom";
import { fetchScenarioQuizzes } from "../../api/quiz/quizScenarioApi";
import { PieChart, Pie, Cell } from "recharts";

import ScenarioHeading from "../../components/quiz/ScenarioHeading";
import ScenarioThumbnailNav from "../../components/quiz/ScenarioThumbnailNav";
import ScenarioProgressBar from "../../components/quiz/ScenarioProgressBar";
import ScenarioPost from "../../components/quiz/ScenarioPost";
import ScenarioOptions from "../../components/quiz/ScenarioOptions";

// Main component for the Scenario-based quiz.
// Displays realistic social media posts and asks users to choose the most responsible action.
const ScenarioPage = () => {
  // State variables
  const [quizzes, setQuizzes] = useState([]); // Loaded quiz data
  const [currentIndex, setCurrentIndex] = useState(0); // Current scenario index
  const [score, setScore] = useState(0); // Total user score
  const [showResult, setShowResult] = useState(false); // Result screen toggle
  const [userAnswers, setUserAnswers] = useState({}); // Stores user's selected options
  const [selectedOptionId, setSelectedOptionId] = useState(null); // Currently selected option
  const [finishWarning, setFinishWarning] = useState(""); // Warning if user tries to finish early
  const [selectedDetail, setSelectedDetail] = useState(null); // For showing full post again on result screen
  const [showUnansweredHighlights, setShowUnansweredHighlights] =
    useState(false);
  const [hasShownAllAnsweredAlert, setHasShownAllAnsweredAlert] =
    useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primary-bg", "#ebebe1");
    root.style.setProperty("--box-bg", "#3e5e48");
    root.style.setProperty("--accent-yellow", "#fbc640");
    root.style.setProperty("--main-green", "#2f472c");
  }, []);

  // Load scenario quiz data on component mount
  useEffect(() => {
    fetchScenarioQuizzes().then(setQuizzes);
  }, []);

  // Handle selecting an option and move to next question
  const handleOptionSelect = (optionId) => {
    const currentQuiz = quizzes[currentIndex];
    const selected = currentQuiz.options.find(
      (opt) => opt.option_id === optionId
    );

    if (selected) {
      const updatedAnswers = {
        ...userAnswers,
        [currentIndex]: {
          scenarioTitle: currentQuiz.title,
          post: currentQuiz.post,
          image_url: currentQuiz.image_url,
          selectedOption: selected,
          tip: currentQuiz.tip,
        },
      };

      setUserAnswers(updatedAnswers);
      setScore((prev) => prev + selected.score);
      setSelectedOptionId(optionId);
      setFinishWarning("");

      const allAnswered = Object.keys(updatedAnswers).length === quizzes.length;
      if (allAnswered && !hasShownAllAnsweredAlert) {
        const confirmed = window.confirm(
          "You finished all the quizzes, do you want to submit the quiz and see the result?"
        );
        setHasShownAllAnsweredAlert(true);
        if (confirmed) {
          setShowResult(true);
        }
      }

      setTimeout(() => {
        if (currentIndex < quizzes.length - 1) {
          setCurrentIndex((prev) => prev + 1);
          setSelectedOptionId(
            updatedAnswers[currentIndex + 1]?.selectedOption?.option_id || null
          );
        }
      }, 200);
    }
  };

  // Manually go to previous or next scenario
  // const handlePrevious = () => {
  //   if (currentIndex > 0) {
  //     setCurrentIndex((prev) => prev - 1);
  //     setSelectedOptionId(
  //       userAnswers[currentIndex - 1]?.selectedOption?.option_id || null
  //     );
  //   }
  // };
  // const handleNext = () => {
  //   if (currentIndex < quizzes.length - 1) {
  //     setCurrentIndex((prev) => prev + 1);
  //     setSelectedOptionId(
  //       userAnswers[currentIndex + 1]?.selectedOption?.option_id || null
  //     );
  //   }
  // };

  // Finalize quiz and show results if all questions are answered
  const handleFinish = () => {
    if (Object.keys(userAnswers).length === quizzes.length) {
      setShowResult(true);
      setFinishWarning("");
      setShowUnansweredHighlights(false);
    } else {
      setFinishWarning(
        "Please answer all scenarios before finishing the quiz."
      );
      setShowUnansweredHighlights(true);
    }
  };

  // Prepare data for pie chart
  const pieData = [
    { name: "Score", value: score },
    { name: "Remaining", value: quizzes.length * 5 - score },
  ];
  const COLORS = ["#fbc640", "#5f6f56"];
  const percent = (score / (quizzes.length * 5)) * 100;

  // Reset the quiz
  const resetQuiz = () => {
    setCurrentIndex(0);
    setScore(0);
    setShowResult(false);
    setUserAnswers({});
    setSelectedOptionId(null);
    setFinishWarning("");
    setSelectedDetail(null);
  };

  // Feedback based on total score
  const getScenarioFeedback = (score) => {
    if (score >= 22) {
      return {
        tier: "🎯 Decision Expert",
        message:
          "Incredible! You make thoughtful and responsible choices online.",
      };
    } else if (score >= 16) {
      return {
        tier: "👍 Responsible Navigator",
        message:
          "Great work! You usually think carefully before you act online.",
      };
    } else if (score >= 8) {
      return {
        tier: "🤔 Growing Citizen",
        message:
          "You're getting better! Keep practicing critical decision-making.",
      };
    } else {
      return {
        tier: "⚠️ At Risk",
        message:
          "Be careful! It's important to think critically before responding online. Try reviewing the tips.",
      };
    }
  };

  // Show loading screen while fetching data
  if (quizzes.length === 0) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading scenario quiz...</p>
      </div>
    );
  }

  const quiz = quizzes[currentIndex];

  return (
    <div className="quiz-wrapper text-center">
      {!showResult ? (
        <>
          <div className="scenario-layout">
            <ScenarioHeading />
            <ScenarioThumbnailNav
              quizzes={quizzes}
              currentIndex={currentIndex}
              userAnswers={userAnswers}
              showUnansweredHighlights={showUnansweredHighlights}
              setCurrentIndex={setCurrentIndex}
              setSelectedOptionId={setSelectedOptionId}
            />
            <ScenarioProgressBar
              currentIndex={currentIndex}
              total={quizzes.length}
            />
            <div className="scenario-horizontal-layout">
              <ScenarioPost quiz={quiz} />
              <ScenarioOptions
                options={quiz.options}
                selectedOptionId={selectedOptionId}
                onSelect={handleOptionSelect}
              />
            </div>
          </div>

          <div className="mt-5 button-row">
            <button
              className="btn btn-warning"
              onClick={() => navigate("/challenge")}
            >
              Back to Challenges
            </button>
            <button className="btn btn-warning" onClick={handleFinish}>
              Submit Quiz
            </button>
          </div>

          {finishWarning && (
            <p className="warning-text mt-2">{finishWarning}</p>
          )}
        </>
      ) : (
        <>
          <h4 className="quiz-performance-heading">
            SCENARIO QUIZ PERFORMANCE
          </h4>
          <div className="performance-summary-box">
            <div className="performance-text">
              {(() => {
                const { tier, message } = getScenarioFeedback(score);
                return (
                  <>
                    <h5>{tier}</h5>
                    <p>{message}</p>
                  </>
                );
              })()}
              <p>
                Score {score}/{quizzes.length * 5}
              </p>
            </div>

            <div className="pie-chart-container">
              <PieChart width={160} height={160}>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
              <div className="pie-center-label">{Math.round(percent)}%</div>
            </div>
          </div>

          <div className="answer-bar-list">
            {Object.values(userAnswers).map((ans, i) => (
              <React.Fragment key={i}>
                <div
                  className="answer-bar-item"
                  onClick={() =>
                    setSelectedDetail(selectedDetail === i ? null : i)
                  }
                >
                  <div className="thumbnail-wrapper">
                    <img
                      src={ans.image_url}
                      alt="scenario"
                      className="thumbnail-img-small"
                    />
                  </div>
                  <div className="bar-item-text">
                    <div className="scenario-title-text">
                      {ans.scenarioTitle}
                    </div>
                    <p>
                      Your Choice: {ans.selectedOption?.option_text} —{" "}
                      <span
                        className={`risk-level-text ${ans.selectedOption?.risk_level
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {ans.selectedOption?.risk_level}
                      </span>
                    </p>
                    <p
                      className={`scenario-feedback-text-bar risk-level-text ${ans.selectedOption?.risk_level
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      📝 {ans.selectedOption?.feedback}
                    </p>
                    {ans.tip && <p className="tip-text-bar">📚 {ans.tip}</p>}
                  </div>
                </div>

                {selectedDetail === i && <ScenarioPost quiz={ans} />}
              </React.Fragment>
            ))}
          </div>

          <div className="result-buttons-wrapper">
            <button
              className="btn btn-warning result-button"
              onClick={resetQuiz}
            >
              TRY AGAIN
            </button>
            <button
              className="btn btn-warning result-button"
              onClick={() => navigate("/challenge")}
            >
              BACK TO CHALLENGES
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ScenarioPage;
